import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Card, Grid, MenuItem, Step, StepLabel, Stepper, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import LayoutPicker from "./components/LayoutPicker";
import DnsInstructions from "./components/DnsInstructions";
import SlugField from "./components/SlugField";
import BrandAssetFields from "./components/BrandAssetFields";
import { CURRENCIES, LOCALES } from "./utils/options";
import { REAL_ESTATE, domainExample, templateVertical, verticalDefaults } from "./utils/vertical";
import { getReVariant } from "layouts/RealEstate/variant";
import { classifyDomain, expectedRecords, isValidDomain, normalizeDomain, platformHostname, slugify } from "./utils/domain";

const STEPS = ["Basics", "Design", "Domain", "Content"];

// The app theme paints the current and finished steps white, for a stepper on
// a coloured header; on this white card they vanished. Brand blue for done
// and current, slate for to-do (both AA on white).
const STEPPER_ON_WHITE = {
  "& .MuiStepIcon-root": { fill: colors.text.light, stroke: colors.text.light, background: colors.text.light, boxShadow: "none" },
  "& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": {
    fill: colors.brand.primary, stroke: colors.brand.primary, background: colors.brand.primary, boxShadow: "none",
  },
  "& .MuiStepLabel-label": { color: `${colors.text.light} !important` },
  "& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed": { color: `${colors.text.dark} !important`, fontWeight: "600 !important" },
  "& .MuiStepConnector-line": { borderColor: colors.border.default },
};

// Locale and currency for a vertical; real estate follows the tenant's variant.
const defaultsFor = (vertical) => verticalDefaults(vertical, getReVariant());

function initialForm(templates) {
  const vertical = templates[0]?.vertical || "travel";
  return {
    name: "",
    brand_name: "",
    tagline: "",
    contact_email: "",
    ...defaultsFor(vertical),
    platform_slug: "",
    template_id: templates[0]?.id || "",
    layout_id: templates[0]?.layouts?.[0]?.id || "",
    domain: "",
    settings: { logo_url: "", favicon_url: "", analytics_id: "", checkout_enabled: false, stripe_enabled: false },
  };
}

function WebsiteCreateWizard({ templates, checkSlug, onCreate, onCancel, saving, onError }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => initialForm(templates));
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ state: "idle" });

  const set = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "name" && !slugTouched) next.platform_slug = slugify(value);
      return next;
    });
  };

  const onSlugChange = (value) => {
    setSlugTouched(true);
    setForm((current) => ({ ...current, platform_slug: value }));
  };

  const onSlugStatus = useCallback((status) => setSlugStatus(status), []);

  const domain = normalizeDomain(form.domain);
  const domainKind = classifyDomain(domain);
  const domainInvalid = Boolean(form.domain.trim()) && !isValidDomain(domain);
  const slugBlocked = ["checking", "taken", "invalid"].includes(slugStatus.state) || slugStatus.slug !== form.platform_slug;
  const emailInvalid = Boolean(form.contact_email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email);

  const canContinue = [
    Boolean(form.name.trim()) && Boolean(form.platform_slug) && !slugBlocked && !emailInvalid,
    Boolean(form.template_id && form.layout_id),
    !domainInvalid,
    true,
  ][step];

  const vertical = templateVertical(templates, form.template_id);
  const realEstate = vertical === REAL_ESTATE;

  // A template from the other vertical brings that vertical's locale and
  // currency, unless they were already changed by hand.
  const onDesignChange = (change) => setForm((current) => {
    const next = { ...current, ...change };
    const before = templateVertical(templates, current.template_id);
    const after = templateVertical(templates, next.template_id);
    const old = defaultsFor(before);
    if (before !== after && current.default_locale === old.default_locale && current.default_currency === old.default_currency) {
      Object.assign(next, defaultsFor(after));
    }
    return next;
  });

  const create = async () => {
    const payload = {
      vertical,
      name: form.name.trim(),
      brand_name: form.brand_name.trim() || null,
      tagline: form.tagline.trim() || null,
      contact_email: form.contact_email.trim() || null,
      default_locale: form.default_locale,
      default_currency: form.default_currency,
      template_id: form.template_id,
      layout_id: form.layout_id,
      platform_slug: form.platform_slug,
      settings: form.settings,
    };
    await onCreate(payload, domain && !domainInvalid ? domain : "");
  };

  const next = () => (step === STEPS.length - 1 ? create() : setStep((current) => current + 1));
  const hostname = slugStatus.hostname || platformHostname(form.platform_slug);

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}` }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
        <MDBox>
          <MDTypography variant="h5">Create a website</MDTypography>
          <MDTypography variant="body2" color="text">Four short steps. You can change everything later.</MDTypography>
        </MDBox>
        {onCancel && <MDButton variant="text" color="dark" onClick={onCancel} disabled={saving}>Cancel</MDButton>}
      </MDBox>
      <Stepper activeStep={step} alternativeLabel sx={{ background: "transparent", boxShadow: "none", p: 0, my: 3, ...STEPPER_ON_WHITE }}>
        {STEPS.map((label, index) => (
          <Step key={label}>
            <StepLabel optional={index === 2 ? <MDTypography variant="caption" color="text">Optional</MDTypography> : null}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><TextField fullWidth required autoFocus label="Website name" value={form.name} onChange={set("name")} helperText="For your team. Visitors see the brand name." /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Brand name" value={form.brand_name} onChange={set("brand_name")} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Tagline" value={form.tagline} onChange={set("tagline")} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth type="email" label="Contact email" value={form.contact_email} onChange={set("contact_email")} error={emailInvalid} helperText={emailInvalid ? "Enter a valid email address." : " "} /></Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth label="Locale" value={form.default_locale} onChange={set("default_locale")}>
              {LOCALES.map((locale) => <MenuItem key={locale.value} value={locale.value}>{locale.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth label="Currency" value={form.default_currency} onChange={set("default_currency")}>
              {CURRENCIES.map((currency) => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <SlugField value={form.platform_slug} onChange={onSlugChange} onStatus={onSlugStatus} checkSlug={checkSlug} disabled={saving} label="PearlCRM website address" />
          </Grid>
        </Grid>
      )}

      {step === 1 && (
        <MDBox>
          <MDTypography variant="body2" color="text" mb={2}>Pick a template and a home page layout. Use Preview to see a layout full size.</MDTypography>
          <LayoutPicker
            templates={templates}
            templateId={form.template_id}
            layoutId={form.layout_id}
            onChange={onDesignChange}
          />
        </MDBox>
      )}

      {step === 2 && (
        <MDBox>
          <MDTypography variant="body2" color="text" mb={2}>
            Your website is always available at <strong>{hostname}</strong>. To use your own domain as well, enter it here.
            You can skip this and connect a domain later.
          </MDTypography>
          <TextField
            fullWidth
            label="Your domain"
            placeholder={domainExample(vertical)}
            value={form.domain}
            onChange={set("domain")}
            error={domainInvalid}
            helperText={domainInvalid ? "Enter a domain like www.example.com, without https://." : "A domain or subdomain you own, without https://."}
          />
          {domainKind && !domainInvalid && (
            <MDBox mt={2}>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>DNS records you&apos;ll add</MDTypography>
              <DnsInstructions
                domain={domain}
                domainKind={domainKind}
                records={expectedRecords(domain, { platformHost: hostname })}
                pendingText="Shown after you save"
              />
            </MDBox>
          )}
        </MDBox>
      )}

      {step === 3 && (
        <MDBox>
          <MDTypography variant="body2" color="text" mb={2}>
            {realEstate
              ? "Add your brand assets. Visitors enquire, book inspections and request appraisals from your listings; you choose which listings to publish after the website is created."
              : <>Add your brand assets and choose how visitors book. You&apos;ll edit page content in the storefront builder after the website is created.</>}
          </MDTypography>
          <BrandAssetFields
            values={form.settings}
            onChange={(field, value) => setForm((current) => ({ ...current, settings: { ...current.settings, [field]: value } }))}
            onError={onError}
            disabled={saving}
            showCheckout={!realEstate}
          />
        </MDBox>
      )}

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3} gap={1} flexWrap="wrap">
        <MDButton variant="outlined" color="dark" disabled={step === 0 || saving} onClick={() => setStep((current) => current - 1)}>Back</MDButton>
        <MDBox display="flex" gap={1}>
          {step === 2 && (
            <MDButton variant="text" color="dark" disabled={saving} onClick={() => { setForm((current) => ({ ...current, domain: "" })); setStep(3); }}>
              Skip for now
            </MDButton>
          )}
          <MDButton color="info" disabled={!canContinue || saving} onClick={next}>
            {step === STEPS.length - 1 ? (saving ? "Creating…" : "Create website") : "Continue"}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

WebsiteCreateWizard.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  checkSlug: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onError: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

WebsiteCreateWizard.defaultProps = { onCancel: null, saving: false };

export default WebsiteCreateWizard;
